import { isPlatformBrowser } from '@angular/common';
import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from '@environments/environment';
import { LocalStorageService } from './core/services/local-storage.service';
import { AppConstant } from './core/constant/app-constant';
import {
  DeviceDetails,
  DeviceScreenResolution,
} from './core/interfaces/device.interface';
import { RoleCode } from './core/enums/role-code.enum';
import { DevicePlatform } from './core/enums/device-platform.enum';
import { UserCountryService } from '@app/public/shared/services/user-country.service';

@Injectable({
  providedIn: 'root',
})
export class AppService {
  baseUrl: string = environment.apiUrl;

  constructor(
    private readonly http: HttpClient,
    private readonly localStorageService: LocalStorageService,
    private readonly userCountryService: UserCountryService,
    @Inject(PLATFORM_ID) private readonly platformId: object
  ) {}

  createUniqueDeviceId() {
    return crypto.randomUUID();
  }

  checkLocalStorageForDeviceId() {
    const deviceId = this.localStorageService.get(AppConstant.Keys.deviceId);
    if (!deviceId || deviceId === 'undefined') {
      this.localStorageService.set(
        AppConstant.Keys.deviceId,
        this.createUniqueDeviceId()
      );
      return this.localStorageService.get(AppConstant.Keys.deviceId) as string;
    }
    return this.localStorageService.get(AppConstant.Keys.deviceId) as string;
  }

  getDeviceDetails(): DeviceDetails {
    const deviceId = this.checkLocalStorageForDeviceId();
    const baseDetails: DeviceDetails = {
      deviceId,
      platform: DevicePlatform.WEB,
      role: null,
      deviceModel: null,
      manufacturer: null,
      osVersion: null,
      appVersion: null,
      language: null,
      timezone: null,
      screenResolution: null,
      fcmToken: null,
      city: null,
      state: null,
      country: null,
    };

    if (!isPlatformBrowser(this.platformId)) {
      return baseDetails;
    }

    const navigatorRef = globalThis.navigator;
    if (!navigatorRef) {
      return baseDetails;
    }

    const role = RoleCode.VIEWER;
    const language = this.sanitizeValue(navigatorRef.language, 10);
    const timezone = this.sanitizeValue(this.getTimeZone(), 100);
    const appVersion = this.sanitizeValue(
      this.localStorageService.get('appVersion'),
      50
    );
    const deviceModel = this.extractDeviceModel(navigatorRef);
    const manufacturer = this.extractManufacturer(navigatorRef);
    const osVersion = this.sanitizeValue(navigatorRef.userAgent, 50);
    const fcmToken = this.getStoredFcmToken();
    const screenResolution = this.getScreenResolution();
    const country = this.getCountryCode();

    return {
      ...baseDetails,
      role,
      deviceModel,
      manufacturer,
      osVersion,
      appVersion,
      language,
      timezone,
      screenResolution,
      fcmToken,
      country,
    };
  }

  sendDeviceDetails() {
    const payload = this.getDeviceDetails();
    return this.http.post<any>(`${this.baseUrl}/user/devices`, payload);
  }

  private getScreenResolution(): DeviceScreenResolution | null {
    if (!isPlatformBrowser(this.platformId)) {
      return null;
    }

    const screenRef = globalThis.screen;
    if (!screenRef) {
      return null;
    }

    const width = Math.max(0, Math.trunc(screenRef.width ?? 0));
    const height = Math.max(0, Math.trunc(screenRef.height ?? 0));

    return {
      width,
      height,
    };
  }

  private getStoredFcmToken(): string | null {
    const storedToken = this.localStorageService.get('FCMToken');
    if (typeof storedToken !== 'string') {
      return null;
    }

    const trimmedToken = storedToken.trim();
    return trimmedToken || null;
  }

  private getTimeZone(): string | null {
    try {
      return Intl.DateTimeFormat().resolvedOptions().timeZone ?? null;
    } catch (error) {
      console.warn('Unable to determine timezone:', error);
      return null;
    }
  }

  private extractDeviceModel(navigatorRef: Navigator): string | null {
    const userAgentData = (
      navigatorRef as Navigator & {
        userAgentData?: { platform?: string; model?: string };
      }
    ).userAgentData;
    const platformInfo = userAgentData?.platform ?? null;
    const modelInfo = userAgentData?.model ?? null;
    const combinedInfo = [platformInfo, modelInfo]
      .filter((value) => !!value)
      .join(' ')
      .trim();

    if (combinedInfo) {
      return combinedInfo.slice(0, 100);
    }

    const userAgent = navigatorRef.userAgent ?? null;
    if (!userAgent) {
      return null;
    }

    return userAgent.slice(0, 100);
  }

  private extractManufacturer(navigatorRef: Navigator): string | null {
    const userAgentData = (
      navigatorRef as Navigator & {
        userAgentData?: { brands?: Array<{ brand: string }> };
      }
    ).userAgentData;

    const brandEntry = userAgentData?.brands?.find(
      (entry) => entry.brand && entry.brand !== 'Not A(Brand)'
    );

    return this.sanitizeValue(brandEntry?.brand ?? null, 100);
  }

  private getCountryCode(): string | null {
    if (!this.userCountryService?._countryCode) {
      return null;
    }

    try {
      const code = this.userCountryService._countryCode();
      return this.sanitizeValue(code, 100);
    } catch (error) {
      console.warn('Unable to fetch country code from UserCountryService:', error);
      return null;
    }
  }

  private sanitizeValue(
    value: string | null | undefined,
    maxLength: number
  ): string | null {
    if (!value) {
      return null;
    }

    const trimmedValue = value.trim();
    if (!trimmedValue) {
      return null;
    }

    return trimmedValue.slice(0, maxLength);
  }
}
